import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, CsvImportPanel, DataTable, EmptyState, FilterBar, FilterChips, FiltroPill, FormActions, FormGrid, IconButton, Pagination,
  NumberField, Pill, SelectField, TextField, TextareaField, type Column,
} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { RegrasRefinanciamentoConvenio } from "../../components/RegrasRefinanciamentoConvenio";
import { QUERY_CREDORES_OPERAVEIS, listarCredoresOperaveis, rotuloCredor } from "../../lib/credores-operaveis";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis, rotuloUnidade } from "../../lib/unidades-operaveis";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";
import { useFiltros } from "../../lib/use-filtros";
import { useDebouncedValue } from "../../lib/use-debounce";
import type {
  AdminBanco, AdminConvenio, AdminConvenioConfig, AdminConvenioInput,
  AdminDeclaracaoVerbaInput, AdminEfeitoDeclaracao,
  AdminPrefeitura, FormatoImportacao, VinculoAceito,
} from "@atlas/sdk";

/**
 * Os tres produtos, com o teto legal de cada um.
 *
 * Os tetos sao INDEPENDENTES — nao ha um total do qual descontar. E' por isso
 * que cada produto precisa da sua propria verba: rubrica somada nao permite
 * conferir nenhum dos tres.
 */
const PRODUTOS = [
  { value: "EMPRESTIMO", label: "Empréstimo" },
  { value: "CARTAO_CONSIGNADO", label: "Cartão consignado" },
  { value: "CARTAO_BENEFICIOS", label: "Cartão benefício" },
] as const;

const ACOES: Record<AdminEfeitoDeclaracao["acao"], string> = {
  abriu: "vigência aberta",
  sucedeu: "sucedeu a anterior",
  corrigiu: "vigência corrigida",
  estendeu: "vigência estendida para trás",
  encerrou: "vigência encerrada",
  inalterado: "sem mudança",
};

function rotuloProduto(p: string): string {
  return PRODUTOS.find((x) => x.value === p)?.label ?? p;
}

/** `0234` e `234` são a mesma verba: comparar cru ofereceria a escolha à toa. */
function canonico(c: string): string {
  const t = c.trim().toUpperCase();
  return /^\d+$/.test(t) ? (t.replace(/^0+/, "") || "0") : t;
}

/**
 * O que mostrar quando o servidor recusa o cadastro.
 *
 * A recusa de verba viaja em `details.verbas[].mensagem` — é lá que está "a
 * competência 202608 já foi consolidada" ou "a verba pertencia a outro
 * vínculo". `err.message` sozinho é só "Dados invalidos", que não diz o que
 * corrigir.
 */
function mensagemDeRecusa(err: unknown): string {
  const base = err instanceof Error ? err.message : "Erro ao salvar";
  const details = (err as { details?: { verbas?: { mensagem?: string }[] } } | null)?.details;
  const verbas = Array.isArray(details?.verbas) ? details.verbas : [];
  const mensagens = verbas.map((v) => v?.mensagem).filter((m): m is string => Boolean(m));
  return mensagens.length > 0 ? mensagens.join("\n") : base;
}

/**
 * `000000` = "desde sempre" no servidor. Na tela vira campo vazio: o operador
 * não precisa conhecer a sentinela, e vazio já significa "o servidor decide".
 */
const DESDE_SEMPRE = "000000";

/**
 * Como o usuário lê a origem da sugestão. É o texto que explica POR QUE o
 * campo veio preenchido com um mês que não é o corrente — sem isso o operador
 * "corrige" para hoje e deixa a carga presa na quarentena.
 */
function explicarSugestao(s: { origem: "quarentena" | "holerite" | "imemorial"; descontosPendentes: number; competenciaSugerida: string } | undefined): string {
  if (!s) return "Deixe em branco para o servidor abrir na competência mais antiga em que esta verba já descontava nesta unidade.";
  if (s.origem === "quarentena") {
    return `Sugerido ${s.competenciaSugerida}: competência mais antiga com desconto desta verba ainda em quarentena (${s.descontosPendentes} pendente(s)). Salvar com ela promove esses descontos a contratos legados.`;
  }
  if (s.origem === "holerite") {
    return `Sugerido ${s.competenciaSugerida}: primeira competência em que esta verba apareceu num holerite desta unidade.`;
  }
  return "Esta verba ainda não apareceu em nenhum holerite nem quarentena: em branco, a vigência vale desde sempre.";
}

const VINCULOS: { value: VinculoAceito; label: string }[] = [
  { value: "CLT", label: "CLT" },
  { value: "ESTATUTARIO", label: "Estatutário" },
  { value: "COMISSIONADO", label: "Comissionado" },
  { value: "APOSENTADO", label: "Aposentado" },
  { value: "PENSIONISTA", label: "Pensionista" },
];

export function AdminConvenios() {
  const qc = useQueryClient();
  const [busca, setBusca] = useState("");
  const [situacao, setSituacao] = useState("");
  const [bancosSel, setBancosSel] = useState<string[]>([]);
  const [prefeiturasSel, setPrefeiturasSel] = useState<string[]>([]);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  // Sem debounce cada tecla e uma queryKey nova, ou seja um request.
  const buscaDebounced = useDebouncedValue(busca);
  // CSV: o formato que a rota e o SDK aceitam pra multi-selecao. Junto aqui
  // (e nao dentro do `queryFn`) pra a `queryKey` nao depender da ORDEM de
  // clique — `["2","7"]` e `["7","2"]` tem que ser a MESMA chave.
  const bancoIdsCsv = bancosSel.length ? bancosSel.join(",") : undefined;
  const unidadeIdsCsv = prefeiturasSel.length ? prefeiturasSel.join(",") : undefined;

  // Busca e filtros sobem para a rota junto com o recorte — subir o recorte
  // sozinho faria a busca procurar apenas dentro da pagina.
  const data = useQuery({
    queryKey: ["admin", "convenios", buscaDebounced, situacao, bancoIdsCsv, unidadeIdsCsv, pagina, porPagina],
    queryFn: () => atlas.admin.listConvenios({
      q: buscaDebounced || undefined,
      // Tres estados: vazio traz ativos E inativos, que e o que a tela sempre
      // mostrou (ela exibe a situacao numa coluna e permite reativar).
      ativo: situacao === "" ? undefined : situacao === "ativo",
      banco_ids: bancoIdsCsv,
      prefeitura_ids: unidadeIdsCsv,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });
  const configs = useQuery({ queryKey: ["admin", "convenios", "configs"], queryFn: () => atlas.admin.listConveniosConfigs() });
  const bancos = useQuery({ queryKey: QUERY_CREDORES_OPERAVEIS, queryFn: listarCredoresOperaveis });
  const prefeituras = useQuery({ queryKey: QUERY_UNIDADES_OPERAVEIS, queryFn: listarUnidadesOperaveis });
  const remove = useMutation({
    mutationFn: (id: string) => atlas.admin.deleteConvenio(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "convenios"] });
      qc.invalidateQueries({ queryKey: ["admin", "convenios", "configs"] });
    },
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => atlas.admin.reativarConvenio(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "convenios"] });
      qc.invalidateQueries({ queryKey: ["admin", "convenios", "configs"] });
    },
  });
  const hardDelete = useMutation({
    mutationFn: (id: string) => atlas.admin.deleteConvenioHard(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "convenios"] });
      qc.invalidateQueries({ queryKey: ["admin", "convenios", "configs"] });
    },
    onError: (e) => alert(`Não foi possível excluir: ${(e as Error).message}`),
  });
  const [editing, setEditing] = useState<AdminConvenio | "new" | null>(null);
  const [configuring, setConfiguring] = useState<AdminConvenio | null>(null);

  const configMap = useMemo(() => {
    const m = new Map<string, AdminConvenioConfig>();
    for (const c of configs.data?.configs ?? []) m.set(c.id, c);
    return m;
  }, [configs.data]);

  // A pagina JA vem filtrada e ordenada por ID (a mesma ordem de sempre).
  // `recorteDoServidor` da a MESMA forma que o recorte no cliente, e e o que
  // deixa a diferenca invisivel para o `Pagination`.
  const daPagina = recorteDoServidor(data.data?.convenios ?? [], data.data?.total ?? 0, pagina, porPagina);
  // Uma lista so', de proposito: contagem do botao e reset saem dela — uma
  // dimensao nova esquecida num dos dois vira filtro que nao conta, ou que
  // "Limpar" nao limpa, e o operador so' descobre quando a grid nao volta ao
  // normal. Usa `busca` (nao a debounced): o botao aparece na primeira tecla,
  // antes de o request sair.
  // Esta era a UNICA tela do repo com esta lista. Agora ela e' o contrato de
  // `useFiltros` (lib/use-filtros): a contagem, o reset, a enfase do botao e o
  // `filtrar` que zera a pagina saem todos daqui. O `filtrar` local saiu — era
  // a mesma funcao redefinida em outros 10 arquivos.
  const filtros = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "situacao", ligado: situacao !== "", limpar: () => setSituacao("") },
      { nome: "prefeitura", ligado: prefeiturasSel.length > 0, limpar: () => setPrefeiturasSel([]) },
      { nome: "banco", ligado: bancosSel.length > 0, limpar: () => setBancosSel([]) },
    ],
    setPagina,
  );
  const { filtrar, temFiltro } = filtros;

  const columns: Column<AdminConvenio>[] = [
    { key: "id", header: "ID", mono: true },
    { key: "nome", header: "Convênio" },
    { key: "prefeituraNome", header: "Órgão/entidade" },
    { key: "bancoNome", header: "Consignatária", render: (c) => c.bancoNome ? c.bancoNome : (
      <span style={{ color: "var(--danger-500)", fontSize: 13 }} title="Consignatária foi deletada — religue o convênio">
        Sem consignatária
      </span>
    ) },
    // As verbas declaradas, uma por produto. Convenio anterior a esta mudanca
    // tem so' `codigoVerba` e aparece sinalizado: ele opera pelo fallback (a
    // verba unica atende os tres produtos, que e' o defeito do `0267`), e o
    // sinal fica ate' alguem declarar produto a produto.
    {
      key: "codigoVerba",
      header: "Verbas",
      render: (c) => {
        const ativas = (c.verbas ?? []).filter((v) => v.situacao === "ativa");
        if (ativas.length > 0) {
          // Declaração PARCIAL ainda opera pelo fallback: o produto sem verba
          // própria origina contrato com a verba única, e o desconto dele chega
          // à prefeitura somado ao de outro produto. É o `0267` de novo, só que
          // pela metade — por isso a linha continua sinalizada até os produtos
          // ofertados terem verba própria (FR-041).
          const semVerbaPropria = PRODUTOS.filter((p) => !ativas.some((v) => v.produto === p.value));
          const caiNoFallback = Boolean(c.codigoVerba) && semVerbaPropria.length > 0;
          return (
            <span style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
              {ativas.map((v) => (
                <code key={v.produto} title={rotuloProduto(v.produto)} style={{ fontSize: 12 }}>
                  {rotuloProduto(v.produto).split(" ")[0]}&nbsp;{v.codigoOriginal}
                </code>
              ))}
              {caiNoFallback ? (
                <span
                  style={{ color: "var(--gold-500)", fontSize: 12 }}
                  title={`${semVerbaPropria.map((p) => p.label).join(" e ")} ainda origina com a verba única ${c.codigoVerba}. Declare a verba de cada produto.`}
                >
                  · {semVerbaPropria.length === 1 ? "1 produto" : `${semVerbaPropria.length} produtos`} na verba única <code>{c.codigoVerba}</code>
                </span>
              ) : null}
            </span>
          );
        }
        if (!c.codigoVerba) {
          return (
            <span style={{ color: "var(--gold-500)", fontSize: 13 }} title="Sem verba declarada: não origina contrato">
              Incompleto
            </span>
          );
        }
        return (
          <span style={{ fontSize: 13 }} title="Verba única atende todos os produtos — declare uma por produto">
            <code>{c.codigoVerba}</code>{" "}
            <span style={{ color: "var(--gold-500)" }}>· produtos sem verba própria</span>
          </span>
        );
      },
    },
    {
      key: "trava",
      header: "Trava",
      render: (c) => {
        const cfg = configMap.get(c.id);
        return cfg ? (
          <span style={{ fontSize: 13 }}>
            {cfg.prazoTravaHoras}h
            <span style={{ color: "var(--text-dim)" }}> / port. {cfg.prazoPortabilidadeDU}du</span>
          </span>
        ) : (
          <span style={{ color: "var(--text-dim)" }}>—</span>
        );
      },
    },
    {
      key: "formato",
      header: "Importação",
      render: (c) => {
        const cfg = configMap.get(c.id);
        return cfg ? <Pill variant="emdia">{cfg.formatoImportacao}</Pill> : <span style={{ color: "var(--text-dim)" }}>—</span>;
      },
    },
    // Herdados do órgão (ADR-0012) — a API já devolve o valor da unidade, não a
    // coluna do convênio. "do órgão" no cabeçalho para que a coluna não seja
    // lida como atributo negociado por banco, que é o que ela era até 15/09.
    { key: "dataCorte", header: "Corte (do órgão)", render: (c) => `dia ${c.dataCorte}` },
    { key: "diaRepasse", header: "Repasse (do órgão)", render: (c) => `dia ${c.diaRepasse}` },
    {
      key: "status",
      header: "Status",
      render: (c) => {
        if (!c.ativo) return <Pill variant="expirado">Inativo</Pill>;
        const cfg = configMap.get(c.id);
        if (!cfg) return <Pill variant="pendente">sem config</Pill>;
        return <Pill variant={cfg.ativo ? "averbado" : "pendente"}>{cfg.ativo ? "Ativo" : "Config off"}</Pill>;
      },
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Averbadora
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Convênios (consignatária × organização)</h1>
          <p style={{ color: "var(--text-muted)", margin: "6px 0 0" }}>
            Relacional consignatária × organização. Cada convênio possui configuração de prazo de trava, regras de produto e formato de importação.
          </p>
        </div>
        <Button onClick={() => setEditing("new")}>+ Novo convênio</Button>
      </header>

      <CsvImportPanel
        title="Importar convênios"
        columnsHint="Colunas: bancoId, unidadeId, nome, verbaEmprestimo, verbaCartaoConsignado, verbaCartaoBeneficio, competenciaInicial, dataCorte, diaRepasse. Coluna de verba vazia = produto não ofertado. dataCorte e diaRepasse continuam sendo aceitas (planilha antiga não quebra) mas não têm mais efeito: corte e repasse são do órgão."
        templateUrl={atlas.admin.csvTemplateUrl("convenios")}
        onImport={async (csv) => atlas.admin.importCsv("convenios", csv)}
        onImported={() => qc.invalidateQueries({ queryKey: ["admin", "convenios"] })}
      />

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 8 }}>
          Editar detalhes · convênio a convênio
        </div>
      <FilterBar
        layout="linha"
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por ID, nome, organização ou verba"
        // O botao fica SEMPRE visivel — e' o padrao de toda tela que usa
        // FilterBar (bancos.tsx, prefeituras.tsx...). O que muda com filtro
        // aplicado e' a EMFASE (`resetActive`) e o rotulo ganhando a
        // contagem, nao a existencia do botao.
        {...filtros.propsFilterBar}
        actions={
          // SEM CONTAGEM: os filtros vao pra API e o numero teria que sair da
          // base inteira, que a tela nao tem.
          // Segmentado, e nao pills soltas: Situacao e' escolha UNICA — ao
          // contrario de Prefeitura/Banco, que combinam entre si.
          <FilterChips
            variant="segmentado"
            label="Situação"
            value={situacao}
            onChange={filtrar(setSituacao)}
            options={[
              { value: "", label: "Todos" },
              { value: "ativo", label: "Ativos" },
              { value: "inativo", label: "Inativos" },
            ]}
          />
        }
      >
        {/* Prefeitura e Banco sao os dois lados do convenio, e COMBINAM: pill
            com multi-selecao em vez de um chip por valor cadastrado — que
            virava uma linha com um chip por banco/prefeitura, e o proprio
            FilterChips avisa que acima de ~5 valores isso e' ruido, nao
            filtro rapido. O gatilho da pill E' o chip removivel: fechada e
            vazia e' neutra, com selecao ganha o realce de "ativo" e um ✕ que
            limpa so' aquela dimensao — sem linha separada de tags repetindo
            o que a pill ja diz. */}
        <FiltroPill
          label="Órgão ou entidade"
          buscaPlaceholder="Buscar organização..."
          opcoes={(prefeituras.data?.prefeituras ?? []).map((p) => ({ value: String(p.id), label: p.nome }))}
          selecionados={prefeiturasSel}
          onChange={filtrar(setPrefeiturasSel)}
          carregando={prefeituras.isLoading}
        />
        {/* Banco corta por `banco_id`, nao por texto: o nome do banco nao vive
            na tabela de convenios (a resposta o resolve contra o cadastro na
            hora). */}
        <FiltroPill
          label="Consignatária"
          buscaPlaceholder="Buscar consignatária..."
          opcoes={(bancos.data?.bancos ?? []).map((b) => ({ value: String(b.id), label: rotuloCredor(b) }))}
          selecionados={bancosSel}
          onChange={filtrar(setBancosSel)}
          carregando={bancos.isLoading}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(c) => c.id}
        loading={data.isLoading}
        // Distinguir "nada bate com o filtro" de "nao ha convenio".
        //
        // Os DOIS textos sao os de antes, palavra por palavra: o piloto do
        // `EmptyState` tem de provar que a extracao nao mexeu em nada, e
        // reescrever a copy aqui misturaria as duas coisas. `onLimpar` fica de
        // fora pelo mesmo motivo — o botao de limpar dentro da tabela seria
        // comportamento novo, e esta tela e' a regua.
        //
        // O motivo sai de `temFiltro` e nao de `motivoVazio(base, recorte)`
        // porque esta rota nao devolve `totalBase`: com o recorte no servidor,
        // "0 linhas" nao diz se o cadastro esta vazio ou se o filtro nao casou.
        // As telas que ja recebem `totalBase` (`pre-reservas`,
        // `motor-rubricas-abas`) usam a via boa; ligar `totalBase` aqui e'
        // mudanca de rota, e vai no grupo que mexer nesta consulta.
        emptyState={
          <EmptyState
            motivo={temFiltro ? "filtro-vazio" : "sem-dados"}
            filtroVazio="Nenhum convênio bate com esses filtros. Limpe os filtros para ver o resto do cadastro."
            semDados="Nenhum convênio cadastrado. Crie um pra ligar consignatária a organização."
          />
        }
        actions={(c) => (
          <>
            <IconButton title="Editar convênio" onClick={() => setEditing(c)}>✎</IconButton>
            <IconButton title="Configurações (prazos, regras)" onClick={() => setConfiguring(c)}>⚙</IconButton>
            {c.ativo ? (
              <IconButton
                title="Desativar convênio"
                danger
                onClick={() => {
                  if (confirm(`Desativar ${c.nome}?\n\nO convênio para de operar, mas nada é apagado — você pode reativar depois.`)) remove.mutate(c.id);
                }}
              >
                ⏸
              </IconButton>
            ) : (
              <>
                <IconButton title="Reativar convênio" onClick={() => reactivate.mutate(c.id)}>▶</IconButton>
                {/* Excluir permanentemente — so aparece em convenios INATIVOS.
                 *  Backend valida que nao ha contratos vinculados antes de
                 *  apagar. Util pra limpar duplicatas geradas por engano. */}
                <IconButton
                  title="Excluir permanentemente (sem contratos vinculados)"
                  danger
                  onClick={() => {
                    if (confirm(`Excluir PERMANENTEMENTE o convênio ${c.nome} (${c.id})?\n\nSó funciona se não houver contratos vinculados. Ação irreversível.`)) hardDelete.mutate(c.id);
                  }}
                >
                  🗑
                </IconButton>
              </>
            )}
          </>
        )}
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="convênios"
      />
      </div>

      {editing ? (
        <ConvenioModal
          initial={editing === "new" ? null : editing}
          bancos={bancos.data?.bancos ?? []}
          prefeituras={prefeituras.data?.prefeituras ?? []}
          onClose={() => setEditing(null)}
        />
      ) : null}

      {configuring ? (
        <ConfigModal
          convenio={configuring}
          initial={configMap.get(configuring.id) ?? null}
          onClose={() => setConfiguring(null)}
        />
      ) : null}
    </div>
  );
}


function ConvenioModal({
  initial, bancos, prefeituras, onClose,
}: {
  initial: AdminConvenio | null;
  bancos: AdminBanco[];
  prefeituras: AdminPrefeitura[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<AdminConvenioInput>({
    id: initial?.id,
    bancoId: initial?.bancoId ?? bancos[0]?.id ?? 1,
    unidadeId: initial?.unidadeId ?? prefeituras[0]?.id ?? 1,
    nome: initial?.nome ?? "",
    codigoVerba: initial?.codigoVerba ?? "",
    dataCorte: initial?.dataCorte ?? 15,
    diaRepasse: initial?.diaRepasse ?? 5,
  });
  const [error, setError] = useState<string | null>(null);

  // AS DECLARAÇÕES — uma por produto ofertado. É isto que classifica a folha:
  // ao salvar, cada verba passa a significar consignação naquela unidade, com o
  // banco do convênio como credor e o produto declarado como balde de margem.
  //
  // Convênio anterior a esta mudança tem `codigoVerba` único e nenhuma
  // declaração. Ele continua operando pelo fallback, e a linha de empréstimo
  // nasce pré-preenchida com a verba antiga para o operador só confirmar.
  const [verbas, setVerbas] = useState<AdminDeclaracaoVerbaInput[]>(() => {
    const existentes = initial?.verbas ?? [];
    if (existentes.length > 0) {
      return existentes.map((v) => ({
        produto: v.produto,
        codigo: v.codigoOriginal,
        competenciaInicial: v.competenciaInicial,
        situacao: v.situacao,
        doCatalogo: v.doCatalogo,
      }));
    }
    if (initial?.codigoVerba) {
      return [{
        produto: "EMPRESTIMO",
        codigo: initial.codigoVerba,
        // Vazio = o servidor decide (a competência mais antiga com desconto
        // pendente). Era o mês corrente, e o mês corrente é exatamente o que
        // deixava a carga do mês anterior presa na quarentena.
        competenciaInicial: "",
        situacao: "ativa",
      }];
    }
    return [];
  });
  const [efeitos, setEfeitos] = useState<AdminEfeitoDeclaracao[]>([]);
  // Competências que o operador digitou à mão, por produto. A sugestão do
  // servidor só preenche campo que ele ainda não tocou — a escolha dele não é
  // contrariada (FR-002).
  const [tocadas, setTocadas] = useState<Set<string>>(() => new Set());

  // A SUGESTÃO DE VIGÊNCIA, por verba da unidade (feature 010). É a mesma regra
  // que o servidor aplica quando a competência vem em branco, exposta para o
  // operador ver ANTES de salvar e entender por que não é o mês corrente.
  const sugestoes = useQuery({
    queryKey: ["admin", "rubricas", "sugestao-vigencia", form.unidadeId],
    queryFn: () => atlas.admin.sugestaoVigencia(form.unidadeId),
    enabled: form.unidadeId != null,
  });
  /** `0234` e `234` são a mesma verba no servidor; a sugestão vem na forma canônica. */
  const sugestaoDe = (codigo: string) => {
    const alvo = canonico(codigo);
    return (sugestoes.data?.sugestoes ?? []).find((s) => s.codigoVerba === alvo);
  };
  /** O que vai no campo: a sugestão, ou vazio quando ela é "desde sempre". */
  const competenciaSugerida = (codigo: string): string => {
    const s = sugestaoDe(codigo);
    return s && s.competenciaSugerida !== DESDE_SEMPRE ? s.competenciaSugerida : "";
  };

  // O QUE JÁ ESTAVA DECLARADO, por produto. É a régua para saber se o código
  // mudou — e a pergunta "mudança ou correção?" só faz sentido quando mudou.
  const declaradasAntes = useMemo(
    () => new Map((initial?.verbas ?? []).map((v) => [v.produto, v])),
    [initial],
  );
  const mudouOCodigo = (v: AdminDeclaracaoVerbaInput) => {
    const antes = declaradasAntes.get(v.produto);
    return Boolean(antes) && canonico(antes!.codigoOriginal) !== canonico(v.codigo);
  };

  const save = useMutation({
    // `correcaoCadastral` só viaja quando o código daquele produto mudou. Numa
    // declaração nova ela não significa nada, e o servidor a lê como "não
    // informe competência" — o vínculo nasceria valendo desde sempre.
    mutationFn: () =>
      atlas.admin.upsertConvenio({
        ...form,
        verbas:
          verbas.length > 0
            ? verbas.map((v) => ({ ...v, correcaoCadastral: mudouOCodigo(v) ? v.correcaoCadastral : undefined }))
            : undefined,
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["admin", "convenios"] });
      // Fecha direto quando nada mudou nas verbas. Havendo efeito — vínculo
      // aberto, dívida reclassificada, quarentena promovida — mostra o que
      // aconteceu antes de sair: é a única chance de o operador ver isso.
      const mudou = (r.declaracoes ?? []).some((d) => d.acao !== "inalterado");
      if (!mudou) onClose();
      else { setEfeitos(r.declaracoes ?? []); setError(null); }
    },
    onError: (err) => setError(mensagemDeRecusa(err)),
  });

  // CANDIDATAS: as verbas ativas do catálogo do BANCO escolhido. É o que faz o
  // segundo convênio do mesmo banco parar de ser digitação do zero. O catálogo
  // é conveniência, não obrigação: dá para declarar verba que não está nele.
  const catalogo = useQuery({
    queryKey: ["admin", "bancos", form.bancoId, "verbas"],
    queryFn: () => atlas.admin.listVerbasBanco(form.bancoId),
    enabled: form.bancoId != null,
  });
  const candidatas = useMemo(
    () => (catalogo.data?.verbas ?? []).filter((v) => v.situacao === "ativa"),
    [catalogo.data],
  );

  const produtosLivres = PRODUTOS.filter((p) => !verbas.some((v) => v.produto === p.value));

  function alterarVerba(i: number, campo: keyof AdminDeclaracaoVerbaInput, valor: string | boolean) {
    const copia = [...verbas];
    const atual = copia[i]!;
    copia[i] = { ...atual, [campo]: valor };
    if (campo === "competenciaInicial") {
      // Digitou: a partir daqui a sugestão não mexe mais neste produto.
      setTocadas((t) => new Set(t).add(atual.produto));
    } else if (campo === "codigo" && typeof valor === "string" && !tocadas.has(atual.produto)) {
      // Trocou o código sem ter mexido na competência: a sugestão acompanha a
      // verba nova, porque é dela que vem a quarentena a alcançar.
      copia[i] = { ...copia[i]!, competenciaInicial: competenciaSugerida(valor) };
    }
    setVerbas(copia);
    setEfeitos([]);
  }

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalCard}>
        <h3 style={{ margin: 0 }}>{initial ? `Editar ${initial.nome}` : "Novo convênio"}</h3>
        <FormGrid cols={2}>
          <SelectField
            label="Consignatária"
            value={String(form.bancoId)}
            onChange={(e) => setForm({ ...form, bancoId: Number(e.target.value) })}
            options={bancos.map((b) => ({ value: String(b.id), label: rotuloCredor(b) }))}
          />
          <SelectField
            label="Órgão/entidade"
            value={String(form.unidadeId)}
            onChange={(e) => setForm({ ...form, unidadeId: Number(e.target.value) })}
            options={prefeituras.map((p) => ({ value: String(p.id), label: rotuloUnidade(p) }))}
          />
          <TextField label="Nome do convênio" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
        </FormGrid>

        {/* CORTE E REPASSE SAÍRAM DAQUI (ADR-0012). Eram dois `NumberField`
            editáveis, um por convênio, e a folha usava o menor entre os
            convênios ativos do órgão — um jeito enviesado de dizer que a data
            nunca foi do acordo comercial. Ela é do órgão, e lá há uma tela.

            A linha fica em vez de sumir: quem abre este formulário procurando
            "dia de corte" precisa descobrir para onde ele foi, e não concluir
            que a informação deixou de existir. */}
        <p style={{ margin: "12px 0 0", fontSize: 12, color: "var(--text-dim)" }}>
          <b style={{ color: "var(--text)" }}>Corte e repasse não são do convênio.</b> Valem para a folha
          inteira do órgão, e são definidos no cadastro dele — mesmos dias para todas as consignatárias conveniadas.{" "}
          {form.unidadeId ? (
            <Link to={`/averbadora/organizacoes/${form.unidadeId}?aba=folha`}>ver o corte deste órgão →</Link>
          ) : null}
        </p>

        {/* ===================== VERBAS DECLARADAS ===================== */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: "1px solid var(--border)", paddingTop: 16 }}>
          <div>
            <h4 style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--text-dim)" }}>
              Verbas na folha
            </h4>
            <p style={{ margin: 0, fontSize: 12, color: "var(--text-dim)", lineHeight: 1.5 }}>
              Uma verba <strong>por produto ofertado</strong>. Salvar aqui é o que faz a folha desta
              unidade reconhecer o desconto como sendo desta consignatária — não é preciso confirmar nada na
              tela de rubricas. Produto que o convênio não oferta simplesmente não é declarado.
              A base pode entrar antes do convênio: ao declarar a verba, a Atlas sugere a competência
              mais antiga em que ela já descontava, para que a dívida que já estava na folha vire
              contrato legado.
            </p>
          </div>

          {verbas.length === 0 ? (
            <p style={{ margin: 0, fontSize: 12, color: "var(--gold-500)" }}>
              Sem verba declarada, este convênio não origina contrato.
            </p>
          ) : null}

          {verbas.map((v, i) => (
            <FormGrid cols={2} key={`verba-${v.produto}-${i}`}>
              <SelectField
                label="Produto"
                value={v.produto}
                onChange={(e) => alterarVerba(i, "produto", e.target.value)}
                options={PRODUTOS.filter((p) => p.value === v.produto || produtosLivres.some((l) => l.value === p.value))}
              />
              {/* Candidatas do catálogo do banco, com escape para digitar: a
                  organização pode usar um código diferente do que o banco usa
                  em outras, e alterar aqui não mexe no catálogo dele. */}
              {candidatas.length > 0 ? (
                <SelectField
                  label="Código da verba"
                  value={v.codigo}
                  onChange={(e) => alterarVerba(i, "codigo", e.target.value)}
                  options={[
                    { value: "", label: catalogo.isLoading ? "Carregando…" : "Selecione…" },
                    ...candidatas.map((cv) => ({
                      value: cv.codigoOriginal,
                      label: `${cv.codigoOriginal} — ${cv.descricao}`,
                    })),
                    ...(v.codigo && !candidatas.some((cv) => cv.codigoOriginal === v.codigo)
                      ? [{ value: v.codigo, label: `${v.codigo} (fora do catálogo)` }]
                      : []),
                  ]}
                  hint="Do catálogo da consignatária. Pode ser alterado: vale o código que a organização usa."
                />
              ) : (
                <TextField
                  label="Código da verba"
                  value={v.codigo}
                  onChange={(e) => alterarVerba(i, "codigo", e.target.value)}
                  maxLength={4}
                  hint="A consignatária ainda não tem catálogo. Até 4 caracteres."
                />
              )}
              <TextField
                label="Vale a partir de"
                value={v.competenciaInicial ?? ""}
                onChange={(e) => alterarVerba(i, "competenciaInicial", e.target.value)}
                placeholder="AAAAMM (em branco: o servidor decide)"
                maxLength={6}
                hint={
                  `${explicarSugestao(sugestaoDe(v.codigo))} ` +
                  "Informe outra competência só se a verba passa a valer de um mês específico; " +
                  "anterior à vigência atual, com a mesma consignatária e produto, estende a vigência para trás."
                }
              />
              <SelectField
                label="Situação"
                value={v.situacao ?? "ativa"}
                onChange={(e) => alterarVerba(i, "situacao", e.target.value)}
                options={[
                  { value: "ativa", label: "Ativa" },
                  { value: "inativa", label: "Inativa (não origina neste produto)" },
                ]}
                hint="Inativar encerra a vigência na competência acima. O histórico fica, e os descontos já atribuídos continuam sendo desta consignatária."
              />
              {/* A escolha só aparece quando o código MUDOU. Numa declaração
                  nova não há passado para corrigir, e oferecer a opção convida
                  a marcar "correção" sem efeito — ou pior, com o efeito errado. */}
              {mudouOCodigo(v) ? (
                <SelectField
                  label="Tipo de alteração"
                  value={v.correcaoCadastral ? "correcao" : "mudanca"}
                  onChange={(e) => alterarVerba(i, "correcaoCadastral", e.target.value === "correcao")}
                  options={[
                    { value: "mudanca", label: `Mudança a partir de ${v.competenciaInicial || "AAAAMM"}` },
                    { value: "correcao", label: "Correção cadastral (sempre foi esta)" },
                  ]}
                  hint={`Era ${declaradasAntes.get(v.produto)?.codigoOriginal}. Mudança preserva as competências anteriores; correção reescreve a vigência atual, inclusive meses já repassados.`}
                />
              ) : null}
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => { setVerbas(verbas.filter((_, j) => j !== i)); setEfeitos([]); }}
                >
                  Remover
                </Button>
              </div>
            </FormGrid>
          ))}

          {produtosLivres.length > 0 ? (
            <div>
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  const p = produtosLivres[0]!;
                  const sugerida = candidatas.find((cv) => cv.produtoSugerido === p.value);
                  setVerbas([...verbas, {
                    produto: p.value as AdminDeclaracaoVerbaInput["produto"],
                    // O produto sugerido do catálogo pré-preenche, e é só isso
                    // que ele faz: quem decide o produto que vale é esta tela.
                    codigo: sugerida?.codigoOriginal ?? "",
                    // A sugestão do SERVIDOR, não o mês corrente: a competência
                    // mais antiga com desconto pendente desta verba na unidade.
                    competenciaInicial: competenciaSugerida(sugerida?.codigoOriginal ?? ""),
                    situacao: "ativa",
                    doCatalogo: Boolean(sugerida),
                  }]);
                  setEfeitos([]);
                }}
              >
                Declarar verba de {produtosLivres[0]!.label}
              </Button>
            </div>
          ) : null}
        </div>

        {error ? <div style={{ color: "var(--danger-500)", fontSize: 13, whiteSpace: "pre-wrap" }}>{error}</div> : null}

        {/* O efeito da declaração. Sem isto o operador salva e não vê que doze
            descontos em quarentena acabaram de virar dívida do banco. */}
        {efeitos.length > 0 ? (
          <div style={{ fontSize: 12, padding: "10px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--success-500) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--success-500) 30%, transparent)", display: "flex", flexDirection: "column", gap: 4 }}>
            <strong>Salvo. O que passou a valer:</strong>
            {efeitos.filter((e) => e.acao !== "inalterado").map((e) => (
              <div key={`${e.produto}-${e.codigoVerba}`}>
                Verba <code>{e.codigoVerba}</code> para {rotuloProduto(e.produto)}
                {e.acao === "estendeu" && e.vigenteDeAntes
                  ? `, vigência estendida de ${e.vigenteDeAntes} para ${e.vigenteDe}`
                  : e.vigenteDe ? `, ${e.acao === "encerrou" ? "até" : "desde"} ${e.vigenteDe}` : ""}
                {" "}({ACOES[e.acao]})
                {/* A DÍVIDA JÁ EXISTIA (ADR-0010): a carga a criou com credor a
                    identificar. O que o convênio faz é ligar o banco a ela — a
                    frase "viraram dívida" dizia o contrário e o cliente apontou
                    (10/09/2026). "Promovido" sobra só para verba que estava sem
                    cadastro e ganhou natureza e produto agora. */}
                {e.dividasReclassificadas > 0 ? ` · ${e.dividasReclassificadas} dívida(s) passaram a ter esta consignatária como credor` : ""}
                {e.descontosPromovidos > 0 ? ` · ${e.descontosPromovidos} desconto(s) que estavam em quarentena por verba sem cadastro foram tombados` : ""}
                {/* Os que ficaram são, quase sempre, anteriores à competência
                    declarada. Dizer o número é o que impede o operador de achar
                    que a fila zerou — e é a alternativa a promovê-los por baixo,
                    que atribuiria a este banco repasse já feito. Dizer COMO
                    alcançá-los é o que faltava (feature 010): em produção o
                    aviso apareceu e o operador não tinha caminho. */}
                {e.descontosPendentes > 0 ? (
                  <span style={{ color: "var(--gold-500)" }}>
                    {" "}· {e.descontosPendentes} desconto(s) continuam pendentes
                    {e.vigenteDe ? ` (anteriores a ${e.vigenteDe})` : ""}
                    {e.competenciaParaAlcancarPendentes
                      ? `. Para alcançá-los, informe ${e.competenciaParaAlcancarPendentes} em "Vale a partir de" e salve de novo: a vigência é estendida para trás.`
                      : ". Eles são de competências anteriores à vigência declarada; informe uma competência igual ou anterior à primeira aparição deles."}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose}>{efeitos.length > 0 ? "Fechar" : "Cancelar"}</Button>
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Salvando..." : "Salvar"}</Button>
        </FormActions>
      </div>
    </div>
  );
}

function ConfigModal({
  convenio, initial, onClose,
}: {
  convenio: AdminConvenio;
  initial: AdminConvenioConfig | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Omit<AdminConvenioConfig, "id" | "atualizadoEm" | "taxaMaxAm" | "maxParcelas"> & { taxaMaxAm: number | ""; maxParcelas: number | "" }>({
    prazoTravaHoras: initial?.prazoTravaHoras ?? 48,
    prazoPortabilidadeDU: initial?.prazoPortabilidadeDU ?? 7,
    // Taxa e parcelas nascem VAZIAS quando o convenio ainda nao tem config —
    // cliente pediu (30/07/2026): nada de numero de exemplo pre-preenchido
    // (antes vinha 72 parcelas e 1.8% a.m. chumbados). O operador digita o
    // valor REAL; o submit bloqueia se ficarem vazios.
    maxParcelas: initial?.maxParcelas ?? "",
    taxaMaxAm: initial?.taxaMaxAm ?? "",
    // Idade tambem nasce vazia (cliente 14/08/2026, mesma regra da taxa): antes
    // vinha 18-80 pre-preenchido, e salvar sem tocar no campo gravava uma faixa
    // que ninguem escolheu — a prefeitura depois lia aquilo como regra sua. A
    // diferenca em relacao a taxa e' que o submit NAO bloqueia se ficar vazio:
    // idade e' informativa, nenhuma validacao recusa averbacao por ela, e exigir
    // o campo forcaria uma decisao sem efeito nenhum.
    idadeMin: initial?.idadeMin,
    idadeMax: initial?.idadeMax,
    vinculosAceitos: initial?.vinculosAceitos ?? ["ESTATUTARIO"],
    formatoImportacao: initial?.formatoImportacao ?? "CSV",
    regrasEspeciais: initial?.regrasEspeciais ?? "",
    vigenciaInicio: initial?.vigenciaInicio ?? new Date().toISOString().slice(0, 10),
    vigenciaFim: initial?.vigenciaFim ?? "",
    ativo: initial?.ativo ?? true,
    // Percentual da comissao no faturamento do banco. Se nao setado, o
    // relatorio /banco/relatorios/faturamento cai em 2%. Armazenado como
    // fracao (0.02 = 2%); a UI edita em percent e converte no submit.
    percentualComissao: initial?.percentualComissao,
  });
  // Copia editavel em PERCENT (ex.: 2 pra 2%). Sincroniza com o form no submit.
  const [pctComissaoUi, setPctComissaoUi] = useState<string>(
    initial?.percentualComissao != null ? String(Number((initial.percentualComissao * 100).toFixed(4))) : "2",
  );
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      // Bloqueia salvar sem taxa/parcelas reais — nada de valor de exemplo.
      if (form.taxaMaxAm === "" || !(Number(form.taxaMaxAm) > 0)) {
        throw new Error("Informe a taxa a.m. real (maior que 0).");
      }
      if (form.maxParcelas === "" || !(Number(form.maxParcelas) >= 1)) {
        throw new Error("Informe o máximo de parcelas real (1 ou mais).");
      }
      const pct = Number(pctComissaoUi);
      const percentualComissao = Number.isFinite(pct) && pct >= 0 ? Number((pct / 100).toFixed(6)) : undefined;
      return atlas.admin.upsertConvenioConfig(convenio.id, {
        ...form,
        taxaMaxAm: Number(form.taxaMaxAm),
        maxParcelas: Number(form.maxParcelas),
        vigenciaFim: form.vigenciaFim || undefined,
        percentualComissao,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "convenios", "configs"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Erro ao salvar"),
  });
  const toggle = (v: VinculoAceito) => {
    setForm((f) => ({
      ...f,
      vinculosAceitos: f.vinculosAceitos.includes(v)
        ? f.vinculosAceitos.filter((x) => x !== v)
        : [...f.vinculosAceitos, v],
    }));
  };
  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...modalCard, maxWidth: 760 }}>
        <h3 style={{ margin: 0 }}>Configuração — {convenio.nome}</h3>
        <p style={{ color: "var(--text-muted)", margin: 0, fontSize: 13 }}>
          Define prazos de trava de margem, regras de produto e formato de importação para este convênio.
        </p>
        <FormGrid cols={3}>
          <NumberField label="Trava regular (horas)" value={form.prazoTravaHoras} min={1} max={720} onChange={(e) => setForm({ ...form, prazoTravaHoras: Number(e.target.value) })} hint="Padrão 48h" />
          <NumberField label="Trava portabilidade (dias úteis)" value={form.prazoPortabilidadeDU} min={1} max={30} onChange={(e) => setForm({ ...form, prazoPortabilidadeDU: Number(e.target.value) })} hint="Padrão 7du" />
          <NumberField label="Máx. parcelas" value={form.maxParcelas} min={1} max={120} placeholder="obrigatório" onChange={(e) => setForm({ ...form, maxParcelas: e.target.value === "" ? "" : Number(e.target.value) })} />
          {/* Taxa UNICA a.m. (cliente 21/07/2026: nao ha mais minima/maxima).
              Campo interno segue `taxaMaxAm` pra nao exigir migracao de dado.
              ATENCAO: este valor E UM TETO, e nao apenas a taxa publicada. O
              comentario anterior dizia o contrario ("representa a taxa praticada,
              nao um teto") e contradizia o codigo. Corrigido em 14/08/2026.
              Desde a spec 014 (29/09/2026) e' teto de CET, nao de juros, e so'
              vale para emprestimo, portabilidade e refinanciamento
              (`assertCetDoProduto`, portal-banco/index.ts). */}
          <NumberField label="Teto de CET a.m. (%)" value={form.taxaMaxAm} min={0} max={20} step={0.01} placeholder="obrigatório" hint="Vale para empréstimo, portabilidade e refinanciamento: CET acima é recusado. Cartão não é limitado por este teto." onChange={(e) => setForm({ ...form, taxaMaxAm: e.target.value === "" ? "" : Number(e.target.value) })} />
          {/* Comissao repassada ao banco parceiro sobre o valor financiado.
              Aparece na tela /banco/relatorios/faturamento (label da coluna e
              soma "Comissao estimada"). */}
          <NumberField
            label="Comissão da consignatária (%)"
            value={pctComissaoUi as unknown as number}
            min={0} max={100} step={0.01}
            onChange={(e) => setPctComissaoUi(e.target.value)}
            hint="Fatia repassada à consignatária sobre o valor financiado. Padrão 2%."
          />
          <NumberField label="Idade mín." value={form.idadeMin} min={0} max={120} onChange={(e) => setForm({ ...form, idadeMin: Number(e.target.value) })} />
          <NumberField label="Idade máx." value={form.idadeMax} min={1} max={120} onChange={(e) => setForm({ ...form, idadeMax: Number(e.target.value) })} />
          <SelectField
            label="Formato de importação"
            value={form.formatoImportacao}
            onChange={(e) => setForm({ ...form, formatoImportacao: e.target.value as FormatoImportacao })}
            options={[{ value: "CSV", label: "CSV" }, { value: "EXCEL", label: "EXCEL" }, { value: "API", label: "API" }]}
          />
          <TextField label="Vigência início" type="date" value={form.vigenciaInicio} onChange={(e) => setForm({ ...form, vigenciaInicio: e.target.value })} />
          <TextField label="Vigência fim (opcional)" type="date" value={form.vigenciaFim ?? ""} onChange={(e) => setForm({ ...form, vigenciaFim: e.target.value })} />
        </FormGrid>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Vínculos aceitos</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
            {VINCULOS.map((v) => {
              const on = form.vinculosAceitos.includes(v.value);
              return (
                <button
                  type="button"
                  key={v.value}
                  onClick={() => toggle(v.value)}
                  style={{
                    padding: "6px 12px", borderRadius: 999, border: "1px solid",
                    borderColor: on ? "var(--emerald-500)" : "var(--border-strong)",
                    background: on ? "var(--emerald-500)" : "transparent",
                    color: on ? "var(--navy-900)" : "var(--text)",
                    cursor: "pointer", fontSize: 13, fontWeight: 600,
                  }}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>
        <TextareaField
          label="Regras especiais"
          value={form.regrasEspeciais}
          onChange={(e) => setForm({ ...form, regrasEspeciais: e.target.value })}
          hint="Ex.: refin permitido após 12 parcelas; portabilidade exige biometria; etc."
          rows={3}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <input id="conv-cfg-ativo" type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} />
          <label htmlFor="conv-cfg-ativo" style={{ fontSize: 13 }}>Convênio vigente / aceitando novas operações</label>
        </div>
        {error ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{error}</div> : null}
        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Salvando..." : "Salvar configuração"}</Button>
        </FormActions>
        <RegrasRefinanciamentoConvenio convenioId={convenio.id} />
      </div>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "grid", placeItems: "center", zIndex: 100, backdropFilter: "blur(6px)",
};
const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 640, width: "calc(100% - 48px)",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
  maxHeight: "calc(100vh - 48px)", overflowY: "auto",
};
